/**
 * Late-binding registry for feature-owned DATA LOADERS the shell consumes
 * (design 5.4; round 9 revision): the registry holds PLAIN async
 * functions, never React Hooks — the platform owns one stable Hook whose
 * useState/useEffect always execute, so the hook count can never change
 * between renders regardless of registration timing (Rules of Hooks).
 * Unregistered loader = safe empty state; a registration after first
 * render is picked up on the next refresh cycle.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

let integrationSummaryLoader = null

/**
 * Bounded freshness for the configured signal. Menus, personal credentials,
 * knowledge corpora and the MCP surface are gated on whether an integration is
 * configured, so configuring one has to take visible effect without a reload of
 * unbounded age. Focus refresh alone is not enough: a tab left open and focused
 * would never re-read. One minute is short enough that enabling an integration
 * feels immediate, long enough to cost one small request per idle minute.
 */
export const INTEGRATION_SUMMARY_MAX_AGE_MS = 60_000

/**
 * The single place any surface asks "has the customer configured this
 * integration?", keyed by entitlement key. Absent means not configured, which
 * hides the surface — the conformance test is what stops that being caused by
 * missing coverage rather than a real answer.
 *
 * Configured is not healthy: an integration whose target is down stays visible.
 */
export function isIntegrationConfigured(summary, integrationKey) {
  return summary?.configured?.[integrationKey] === true
}

/**
 * The application composition root registers a plain async
 * () => normalizedSummary (throws on failure). NOT a Hook.
 */
export function registerIntegrationSummaryLoader(fn) {
  integrationSummaryLoader = fn
}

export function useIntegrationSummaryRegistered() {
  const mountedRef = useRef(true)
  // {} is the safe empty: consumers use summary[key]?.field access.
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    const loader = integrationSummaryLoader
    if (!loader) {
      // No provider composed (yet): settle into the empty state; a later
      // refresh (focus, manual) re-reads the registry.
      if (mountedRef.current) {
        setSummary({})
        setLoading(false)
      }
      return
    }
    if (mountedRef.current) setLoading(true)
    try {
      const next = await loader()
      if (mountedRef.current) {
        setSummary(next)
        setError(null)
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.response?.data?.error || err.message || 'Failed to load integration summary')
      }
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    refresh()
    const refreshOnFocus = () => refresh()
    window.addEventListener('focus', refreshOnFocus)
    // Bounded age as well as focus: a tab that stays focused must still notice
    // an integration being configured elsewhere.
    const timer = setInterval(refresh, INTEGRATION_SUMMARY_MAX_AGE_MS)
    return () => {
      mountedRef.current = false
      clearInterval(timer)
      window.removeEventListener('focus', refreshOnFocus)
    }
  }, [refresh])

  return { summary, loading, error, refresh }
}
